import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, Lock } from 'lucide-react';
import { useConsent } from '../hooks/useConsent';

/**
 * CNIL-compliant cookie consent banner.
 *
 * Design principles enforced here:
 *  - The "necessary" category is locked ON and visually distinct
 *    (green pill, padlock icon, no toggle).
 *  - Every other category defaults to OFF and uses a neutral gray toggle
 *    that turns green only when explicitly enabled.
 *  - The three action buttons ("Tout accepter", "Tout refuser",
 *    "Enregistrer mes choix") share the exact same neutral styling so no
 *    button is visually privileged over the others — this is the key
 *    requirement of the CNIL 2020 guidelines.
 */
const Toggle = ({ checked, onChange, locked, testId, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    data-testid={testId}
    disabled={locked}
    onClick={() => !locked && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200
      ${locked
        ? 'bg-emerald-600 cursor-not-allowed'
        : checked
          ? 'bg-emerald-600'
          : 'bg-slate-400 hover:bg-slate-500'}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200
        ${checked || locked ? 'translate-x-5' : 'translate-x-0.5'}`}
    />
  </button>
);

const Category = ({ title, description, checked, onChange, locked, testIdPrefix }) => (
  <div className="flex items-start gap-3 py-2.5 border-t border-white/10 first:border-t-0">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        {locked && <Lock className="w-3 h-3 text-emerald-300" aria-hidden="true" />}
        <span className="font-bold text-[13px] text-white">{title}</span>
      </div>
      <p className="text-[11.5px] text-white/70 leading-snug mt-0.5">{description}</p>
    </div>
    <Toggle
      checked={checked}
      onChange={onChange}
      locked={locked}
      testId={`${testIdPrefix}-toggle`}
      ariaLabel={title}
    />
  </div>
);

const CookieConsent = () => {
  const { t } = useTranslation();
  const { hasDecided, acceptAll, refuseAll, saveChoices } = useConsent();
  const [prefs, setPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  // Reset toggles to defaults whenever the banner reopens (user revoked).
  useEffect(() => {
    if (!hasDecided) {
      setPrefs(false);
      setAnalytics(false);
    }
  }, [hasDecided]);

  if (hasDecided) return null;

  // Unified neutral button — same styling for the three actions so no choice
  // is visually privileged (CNIL 2020 recommendation).
  const neutralBtn =
    'px-3 py-2 rounded-md text-[12.5px] font-bold bg-slate-200 text-slate-800 hover:bg-slate-300 transition-colors border border-slate-300';

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-label={t('cookies.title')}
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 z-[9999] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-[480px]"
    >
      <div className="bg-ter-blue text-white shadow-2xl rounded-xl border border-ter-yellow/40 overflow-hidden">
        <div className="px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-md bg-ter-yellow text-ter-blue flex items-center justify-center">
              <Cookie className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-[15px]">{t('cookies.title')}</div>
              <p className="text-[12px] text-white/85 leading-snug mt-0.5">
                {t('cookies.intro_v2')}
              </p>
            </div>
          </div>

          <div className="mt-3 bg-white/5 rounded-lg px-3 py-1">
            <Category
              testIdPrefix="cookie-cat-necessary"
              locked
              checked
              onChange={() => {}}
              title={t('cookies.cat_necessary')}
              description={t('cookies.cat_necessary_desc')}
            />
            <Category
              testIdPrefix="cookie-cat-preferences"
              checked={prefs}
              onChange={setPrefs}
              title={t('cookies.cat_preferences')}
              description={t('cookies.cat_preferences_desc')}
            />
            <Category
              testIdPrefix="cookie-cat-analytics"
              checked={analytics}
              onChange={setAnalytics}
              title={t('cookies.cat_analytics')}
              description={t('cookies.cat_analytics_desc')}
            />
          </div>

          <p className="text-[10.5px] text-white/60 mt-2 leading-snug">{t('cookies.no_tracking_v2')}</p>
        </div>

        <div className="px-4 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={refuseAll}
            data-testid="cookie-consent-refuse-all"
            className={neutralBtn}
          >
            {t('cookies.refuse_all')}
          </button>
          <button
            type="button"
            onClick={() => saveChoices({ preferences: prefs, analytics })}
            data-testid="cookie-consent-save-choices"
            className={neutralBtn}
          >
            {t('cookies.save_choices')}
          </button>
          <button
            type="button"
            onClick={acceptAll}
            data-testid="cookie-consent-accept-all"
            className={neutralBtn}
          >
            {t('cookies.accept_all')}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Footer link that lets the user reopen the banner to change their mind. */
export const CookieReopenLink = () => {
  const { t } = useTranslation();
  const { reset, hasDecided } = useConsent();
  if (!hasDecided) return null;
  return (
    <button
      type="button"
      onClick={reset}
      className="text-[11px] text-white/60 hover:text-white underline underline-offset-2 inline-flex items-center gap-1"
      data-testid="cookie-consent-reopen"
    >
      <Cookie className="w-3 h-3" /> {t('cookies.manage')}
    </button>
  );
};

export default CookieConsent;
